import { useState } from 'react'
import { motion } from 'framer-motion'
import { Mail, Linkedin } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TeamMember } from '../hooks/useTeamMembers'

interface TeamMemberProps {
  member: TeamMember
  index: number
}

function XingIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M17.436 3.359a.577.577 0 0 0-.525.311L9.466 16.683l-2.637-4.423a.576.576 0 0 0-.507-.292H3.593a.577.577 0 0 0-.509.855l5.082 8.35a.577.577 0 0 0 .993-.013l9.745-17.058a.577.577 0 0 0-.503-.857h-2.465z"/>
    </svg>
  )
}

export function TeamMember({ member, index }: TeamMemberProps) {
  const navigate = useNavigate()
  const { lang } = useParams<{ lang?: string }>()
  const { t } = useTranslation('common')
  const currentLang = lang || 'en'

  // Generate fallback image URLs based on member ID - prioritize local static images
  const getFallbackImageUrls = (memberId: string): string[] => {
    const urls: string[] = []
    
    // FIRST: Try local static images (these always work, even offline)
    if (memberId === 'jan-pfrommer') {
      urls.push('/jan.png', '/images/team/jan.png')
    } else if (memberId === 'kai-bennett') {
      urls.push('/kai.png', '/images/team/kai.png')
    }
    
    // THEN: Try image from member data (might be from API)
    if (member.imageUrl && !urls.includes(member.imageUrl)) {
      urls.push(member.imageUrl)
    }
    
    console.log(`[TeamMember] Image URLs for ${member.name} (${memberId}):`, urls)
    
    return urls
  }

  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const fallbackImageUrls = getFallbackImageUrls(member.id)
  const currentImageUrl = fallbackImageUrls[currentImageIndex]

  const handleImageError = () => {
    // Try next fallback URL
    if (currentImageIndex < fallbackImageUrls.length - 1) {
      setCurrentImageIndex(prev => prev + 1)
    } else {
      // All images failed, show initials
      const img = document.querySelector(`[data-member-id="${member.id}"] img`) as HTMLImageElement
      if (img) {
        img.style.display = 'none'
        const parent = img.parentElement
        if (parent) {
          const fallback = parent.querySelector('.initials-fallback') as HTMLElement
          if (fallback) fallback.style.display = 'flex'
        }
      }
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ y: -8 }}
      className="group h-full"
    >
      <div
        onClick={() => navigate(`/${currentLang}/team/${member.id}`)}
        className="block h-full bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
        data-member-id={member.id}
      >
        {/* Image Section */}
        <div className="relative h-80 overflow-hidden bg-gradient-to-br from-blue-500 to-purple-600 dark:from-orange-500 dark:to-red-500">
          {currentImageUrl ? (
            <img
              key={currentImageIndex}
              src={currentImageUrl}
              alt={member.name}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              onError={handleImageError}
            />
          ) : null}
          {/* Fallback placeholder with initials */}
          <div
            className={`initials-fallback w-full h-full items-center justify-center ${currentImageUrl ? 'hidden' : 'flex'}`}
          >
            <span className="text-6xl font-bold text-white/80">
              {member.name.split(' ').map(n => n[0]).join('')}
            </span>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-6">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {member.name}
          </h3>
          <p className="text-blue-600 dark:text-orange-400 font-semibold mb-4">
            {member.role}
          </p>
          <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-6">
            {member.bio}
          </p>

          {/* Social Links */}
          {(member.email || member.linkedin || member.xing) && (
            <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              {member.email && (
                <a
                  href={`mailto:${member.email}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-orange-400 transition-colors"
                  aria-label={`Email ${member.name}`}
                >
                  <Mail className="w-5 h-5" />
                  <span className="text-sm">{t('social.email')}</span>
                </a>
              )}
              {member.linkedin && (
                <a
                  href={member.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-orange-400 transition-colors"
                  aria-label={`${member.name}'s LinkedIn`}
                >
                  <Linkedin className="w-5 h-5" />
                  <span className="text-sm">{t('social.linkedin')}</span>
                </a>
              )}
              {member.xing && (
                <a
                  href={member.xing}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-orange-400 transition-colors"
                  aria-label={`${member.name}'s Xing`}
                >
                  <XingIcon className="w-5 h-5" />
                  <span className="text-sm">{t('social.xing')}</span>
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}
